import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { useSelector } from 'react-redux';

import { Text, TextType } from 'ui-components/components/text';
import { getPoundsAndOuncesFromOunces } from 'ui-components/components/weight-input/ounces-weight-input';
import { getKilogramsAndGramsFromGrams } from 'ui-components/components/weight-input/helpers';

import { convertWeight } from '../../../../../../services/unit-services/weights/weight-convert-service';
import Global from '../../../../../../store/global-selectors';
import { messages } from '../messages';

const getRoundedPoundsAndOunces = (value: number) => {
  const { pounds, ounces } = getPoundsAndOuncesFromOunces(value);
  const roundedOunces = Math.round((ounces ?? 0) * 1000) / 1000;
  return { pounds, ounces: roundedOunces };
};

type WeightCellProps = {
  value: { value: number; unit?: string } | null;
};

export const WeightCell = ({ value }: WeightCellProps) => {
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);

  if (value === null) {
    return null;
  }

  const sourceUnit = value.unit || sellerWeightUnit;
  const { value: convertedToBase } = convertWeight({ value: value.value, unit: sourceUnit }, sellerWeightUnit);

  const isMetric = sellerWeightUnit?.toLowerCase() === 'grams' || sellerWeightUnit?.toLowerCase() === 'gram';

  if (isMetric) {
    const { kilograms, grams } = getKilogramsAndGramsFromGrams(convertedToBase);
    const roundedGrams = Math.round(grams);
    if (kilograms === 0) {
      return <Text textType={TextType.Paragraph} value={`${roundedGrams} g`} />;
    }
    return <Text textType={TextType.Paragraph} value={`${kilograms} kg ${roundedGrams} g`} />;
  }

  const { pounds, ounces } = getRoundedPoundsAndOunces(convertedToBase);
  if (pounds === 0) {
    return <Text textType={TextType.Paragraph} value={`${ounces} oz`} />;
  }
  return <Text textType={TextType.Paragraph} value={`${pounds} lbs ${ounces} oz`} />;
};

const getWeightUnitMessage = (unit: string) => {
  const lowerUnit = unit.toLowerCase();
  if (lowerUnit === 'grams' || lowerUnit === 'gram') return messages.kg;
  if (lowerUnit === 'kilograms' || lowerUnit === 'kilogram') return messages.kg;
  if (lowerUnit === 'pounds' || lowerUnit === 'pound') return messages.lbs;
  if (lowerUnit === 'ounces' || lowerUnit === 'ounce') return messages.lbs;
  return messages.lbs;
};

type HeaderCellProps = {
  sellerDefaultWeightUnit: string;
  headerMessage: MessageDescriptor;
};

export const WeightHeaderCell = ({ sellerDefaultWeightUnit, headerMessage }: HeaderCellProps) => {
  const intl = useIntl();
  const unitMessage = useMemo(() => getWeightUnitMessage(sellerDefaultWeightUnit), [sellerDefaultWeightUnit]);
  const formattedUnit = intl.formatMessage(unitMessage);
  const headerText = intl.formatMessage(headerMessage, { unit: formattedUnit });

  return <>{headerText}</>;
};
